import { db, storage } from "../firebase.js";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";

function buildDocumentPath(...segments) {
  return segments.filter(Boolean).join("/");
}

function isLikelyImageReference(value) {
  if (typeof value !== "string") {
    return false;
  }

  return (
    value.startsWith("gs://") ||
    /\.(avif|gif|jpe?g|png|webp)(\?.*)?$/i.test(value)
  );
}

function normalizeImageValues(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === "string");
  }

  if (typeof value === "string") {
    return [value];
  }

  return [];
}

function extractImagePaths(docData, imagesFieldName) {
  const candidateKeys = [
    imagesFieldName,
    "Images",
    "images",
    "Image",
    "image",
    "Photos",
    "photos",
    "Photo",
    "photo",
  ];

  const collected = [];

  candidateKeys.forEach((key) => {
    if (key in docData) {
      collected.push(...normalizeImageValues(docData[key]));
    }
  });

  if (collected.length === 0) {
    Object.entries(docData).forEach(([key, value]) => {
      if (/image|photo/i.test(key)) {
        collected.push(...normalizeImageValues(value));
      }
    });
  }

  const imagePaths = collected.filter(isLikelyImageReference);
  return [...new Set(imagePaths)];
}

export function normalizeStoragePath(path) {
  if (typeof path !== "string") {
    return "";
  }

  if (path.startsWith("gs://")) {
    const parts = path.split("/");
    return parts.slice(3).join("/");
  }

  return path;
}

export async function fetchImageEntriesByPath(documentPath, imagesFieldName = "Images") {
  const docRef = doc(db, documentPath);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return [];
  }

  const imagePaths = extractImagePaths(docSnap.data(), imagesFieldName);
  const results = await Promise.allSettled(
    imagePaths.map(async (path) => {
      const imageRef = ref(storage, normalizeStoragePath(path));
      const url = await getDownloadURL(imageRef);

      return {
        name: normalizeStoragePath(path).split("/").pop() || path,
        path,
        url,
      };
    })
  );

  return results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value);
}

export async function fetchImageEntries(collectionName, documentId, imagesFieldName = "Images") {
  return fetchImageEntriesByPath(buildDocumentPath(collectionName, documentId), imagesFieldName);
}

export async function fetchImagesByPath(documentPath, imagesFieldName = "Images") {
  const imageEntries = await fetchImageEntriesByPath(documentPath, imagesFieldName);
  return imageEntries.map((entry) => entry.url);
}

/**
 * Fetch image URLs from any collection/document in Firestore.
 * @param {string} collectionName - e.g. "Summer"
 * @param {string} documentId - e.g. "d6Ovg8OSOCjPWbjbdtKg"
 * @param {string} imagesFieldName - e.g. "Images" (default), can also be "images"
 * @returns {Promise<string[]>} array of download URLs
 */
export async function fetchImages(collectionName, documentId, imagesFieldName = "Images") {
  return fetchImagesByPath(buildDocumentPath(collectionName, documentId), imagesFieldName);
}
export default fetchImages;
